import { useRef, useCallback } from "react";

const CHECK_INTERVAL  = 8000;  // ms between checks
const ALERT_COOLDOWN  = 30000; // ms between same-type alerts

export function useExternalMonitor({ onViolation, enabled = true }) {
  const lastAlertRef    = useRef({});
  const intervalRef     = useRef(null);
  const baselineRef     = useRef(null);

  const isCooledDown = useCallback((type) => {
    const last = lastAlertRef.current[type] || 0;
    return Date.now() - last > ALERT_COOLDOWN;
  }, []);

  const markAlerted = useCallback((type) => {
    lastAlertRef.current[type] = Date.now();
  }, []);

  const flag = useCallback((type, message, details = {}) => {
    if (!isCooledDown(type)) return;
    markAlerted(type);
    onViolation?.({
      type:     "external_monitor",
      severity: "medium",
      message,
      details:  { signal: type, ...details },
    });
  }, [isCooledDown, markAlerted, onViolation]);

  const check = useCallback(() => {
    if (!enabled) return;

    const s = window.screen;

    // Signal 1 — Screen Extended API (Chrome 100+)
    // true when a second monitor is connected
    if (s.isExtended === true) {
      flag(
        "screen_extended",
        "External monitor detected",
        { isExtended: true }
      );
    }

    // Signal 2 — Window position outside primary screen bounds
    // Candidate dragged browser to second monitor
    const outsideX = window.screenX < 0 ||
      window.screenX > s.width + 200;
    const outsideY = window.screenY < 0 ||
      window.screenY > s.height + 200;

    if (outsideX || outsideY) {
      flag(
        "window_outside_screen",
        "Browser window moved outside primary screen",
        {
          screenX: window.screenX,
          screenY: window.screenY,
          screenWidth: s.width,
          screenHeight: s.height,
        }
      );
    }

    // Signal 3 — Window wider than primary screen
    // Indicates window spanning two monitors
    if (window.outerWidth > s.width + 100) {
      flag(
        "window_wider_than_screen",
        "Browser window spans multiple screens",
        {
          outerWidth: window.outerWidth,
          screenWidth: s.width,
        }
      );
    }

    // Signal 4 — Screen resolution changed since interview started
    // Plugging in a monitor mid-interview changes screen.width
    if (baselineRef.current) {
      const { width, height } = baselineRef.current;
      if (s.width !== width || s.height !== height) {
        flag(
          "screen_resolution_changed",
          "Screen resolution changed during interview",
          {
            baseline: `${width}x${height}`,
            current:  `${s.width}x${s.height}`,
          }
        );
        // Update baseline to avoid repeated alerts
        baselineRef.current = { width: s.width, height: s.height };
      }
    }
  }, [enabled, flag]);

  const start = useCallback(() => {
    if (!enabled) return;

    // Capture baseline screen dimensions at interview start
    baselineRef.current = {
      width:  window.screen.width,
      height: window.screen.height,
    };

    // Run immediately then on interval
    check();
    intervalRef.current = setInterval(check, CHECK_INTERVAL);
    console.log("✅ External monitor detection started");
  }, [enabled, check]);

  const stop = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current   = null;
    baselineRef.current   = null;
    lastAlertRef.current  = {};
    console.log("🛑 External monitor detection stopped");
  }, []);

  return { start, stop };
}
